/**
 * Tracks in-progress Review & Clear scans across the app (A2F-461).
 *
 * Lightweight, same-session only — state resets on page refresh.
 * Used by:
 *  - Review/Clear pages to register/clear scans and prevent duplicates
 *  - Dashboard to show scanning indicators on metric cards
 */

import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export type ScanFeature =
  | 'abbreviations'
  | 'compound-code'
  | 'cross-references'
  | 'dose-unit'
  | 'rename-consistency'
  | 'section-owners'
  | 'study-profile';

interface ScanEntry {
  feature: ScanFeature;
  startedAt: number;
}

interface ScanStatusContextValue {
  /** Currently in-progress scans. */
  activeScans: ScanEntry[];
  /** Check if a specific feature is currently scanning. */
  isScanning: (feature: ScanFeature) => boolean;
  /** Check if ANY Review feature (not section-owners) is scanning. */
  isAnyReviewScanning: () => boolean;
  /** Register a scan as started. Returns false if already in progress (caller should skip). */
  startScan: (feature: ScanFeature) => boolean;
  /** Mark a scan as finished (success or error). */
  endScan: (feature: ScanFeature) => void;
}

const ScanStatusContext = createContext<ScanStatusContextValue>({
  activeScans: [],
  isScanning: () => false,
  isAnyReviewScanning: () => false,
  startScan: () => true,
  endScan: () => {},
});

export function useScanStatus() {
  return useContext(ScanStatusContext);
}

export function ScanStatusProvider({ children }: { children: ReactNode }) {
  const [scans, setScans] = useState<ScanEntry[]>([]);
  // Ref mirrors state for synchronous reads inside callbacks
  const scansRef = useRef<ScanEntry[]>([]);

  const isScanning = useCallback((feature: ScanFeature) => {
    return scansRef.current.some(s => s.feature === feature);
  }, []);

  const isAnyReviewScanning = useCallback(() => {
    return scansRef.current.some(s => s.feature !== 'section-owners');
  }, []);

  const startScan = useCallback((feature: ScanFeature): boolean => {
    if (scansRef.current.some(s => s.feature === feature)) {
      return false; // already in progress
    }
    const entry: ScanEntry = { feature, startedAt: Date.now() };
    scansRef.current = [...scansRef.current, entry];
    setScans(scansRef.current);
    return true;
  }, []);

  const endScan = useCallback((feature: ScanFeature) => {
    scansRef.current = scansRef.current.filter(s => s.feature !== feature);
    setScans(scansRef.current);
  }, []);

  return (
    <ScanStatusContext.Provider
      value={{ activeScans: scans, isScanning, isAnyReviewScanning, startScan, endScan }}
    >
      {children}
    </ScanStatusContext.Provider>
  );
}
